import * as React from "react";
import classnames from "classnames";
import { useKey } from "react-use";
import { useTranslation } from "next-i18next";
import { KeyEnum } from "@/components/KeyboardAccessibleDiv";
import { OptionType } from "@/services/type";
import { Direction, findNewIndex, KeyboardAccessibleListProps, setScrollPosition } from "@/components/KeyboardAccessibleList/helper";
function KeyboardAccessibleList<ValueType, OT extends OptionType<ValueType> = OptionType<ValueType>>({
  isEnable,
  isLoading,
  options,
  selectedValues,
  onEnter,
  onClick,
  onEscape,
  renderItem,
  containerClasses,
  isActiveElement = () => true
}: KeyboardAccessibleListProps<ValueType, OT>) {
  // =========================================
  // Init
  // =========================================
  const {
    t
  } = useTranslation();
  const listContainerRef = React.useRef<HTMLUListElement>(null);
  const [isSelectingIndex, setIsSelectingIndex] = React.useState<number | null>(null);
  const [selectedIndexes, setSelectedIndexes] = React.useState<number[]>([]);
  const isInteractive = isEnable && !isLoading && options.length > 0;

  // =========================================
  // Handlers
  // =========================================
  useKey(KeyEnum.escape, event => {
    if (isEnable && onEscape) {
      event.preventDefault();
      event.stopPropagation();
      onEscape();
    }
  }, {
    event: "keydown"
  }, [onEscape, isEnable]);
  useKey(KeyEnum.enter, event_0 => {
    if (isInteractive && onEnter && typeof isSelectingIndex === "number") {
      const foundedOption = options[isSelectingIndex];
      if (foundedOption) {
        const isActive = isActiveElement(foundedOption);
        if (isActive) {
          event_0.preventDefault();
          event_0.stopPropagation();
          onEnter(foundedOption, isSelectingIndex);
        }
      }
    }
  }, {
    event: "keydown"
  }, [onEnter, options, isSelectingIndex, isInteractive, isActiveElement]);
  useKey(KeyEnum.arrowUp, event_1 => {
    if (isInteractive && typeof isSelectingIndex === "number") {
      event_1.preventDefault();
      event_1.stopPropagation();
      const newIndex = findNewIndex(options, isSelectingIndex, Direction.up, isActiveElement);
      setIsSelectingIndex(newIndex);
    }
  }, {
    event: "keydown"
  }, [isInteractive, isSelectingIndex, options, isActiveElement]);
  useKey(KeyEnum.arrowDown, () => {
    if (isInteractive) {
      const newIndex_0 = findNewIndex(options, isSelectingIndex === null ? -1 : isSelectingIndex, Direction.down, isActiveElement);
      setIsSelectingIndex(newIndex_0);
    }
  }, {
    event: "keydown"
  }, [isInteractive, isSelectingIndex, options, isActiveElement]);
  React.useEffect(() => {
    if (isEnable && !isLoading) {
      if (options.length > 0) {
        let lastSelectedIndex = null;
        const selectedListIndexes: number[] = [];
        selectedValues.forEach(selectedItem => {
          const foundedIndex = options.findIndex(({
            value
          }) => selectedItem === value);
          if (foundedIndex !== -1) {
            lastSelectedIndex = foundedIndex;
            selectedListIndexes.push(foundedIndex);
          }
        });
        setIsSelectingIndex(lastSelectedIndex);
        setSelectedIndexes(selectedListIndexes);
      } else {
        setIsSelectingIndex(null);
        setSelectedIndexes([]);
      }
    }
  }, [isEnable, isLoading, options, selectedValues]);
  React.useEffect(() => {
    if (isEnable && !isLoading && typeof isSelectingIndex === "number") {
      setScrollPosition(listContainerRef.current, isSelectingIndex);
    }
  }, [isEnable, isLoading, isSelectingIndex]);

  // =========================================
  // Subcomponent
  // =========================================
  const emptyItem = <li className={"item-empty"}>{t("Keine Vorschläge verfügbar")}</li>;
  const loadingItem = <li className={"item-loading"}>{t("Lädt...")}</li>;
  const generateList = () => {
    const items = [];
    for (let index = 0; index < options.length; index++) {
      const option = options[index];
      const isActive_0 = isActiveElement(option);
      const isHighlighted = selectedIndexes.indexOf(index) !== -1;
      items.push(<li className={classnames("item", {
        deactivate: !isActive_0,
        highlighted: isActive_0 && isHighlighted,
        isSelecting: isActive_0 && isSelectingIndex === index
      })} key={`${String(`${option.value}`)}_${index}`} onMouseDown={event_2 => {
        if (isActive_0) {
          event_2.stopPropagation();
          event_2.preventDefault();
          onClick?.(option, index);
        }
      }}>
          {renderItem ? renderItem(option) : option.label}
        </li>);
    }
    return items;
  };
  const items_0 = options && options.length > 0 ? generateList() : emptyItem;

  // =========================================
  // Render
  // =========================================
  if (!isEnable) return null;
  return <ul ref={listContainerRef} className={classnames("w-full m-0 p-0 flex flex-col", containerClasses)}>
      {isLoading ? loadingItem : items_0}
    </ul>;
}
export default KeyboardAccessibleList;